import { useState, useCallback, useEffect } from 'react';

import { useGetScreenSize, ScreenSize, useWindowScroll } from '../../hooks';

import { NAVIGATION_OFFSET, NAVIGATION_MOBILE_OFFSET } from './Navigation.constants';
import { NavigationPosition, NavigationPositionState, UseNavigationPositionOptions } from './Navigation.types';

/**
 * Хук для управления позицией Navigation при скролле.
 * Предотвращает наезд Navigation на Footer, переключая позиционирование
 * с fixed на absolute при достижении подвала.
 *
 * @param footerRef - Ref на элемент Footer
 * @returns Объект со стилями позиционирования для Navigation
 */
export const useNavigationPosition = ({ footerRef }: UseNavigationPositionOptions): NavigationPositionState => {
    const screenSize = useGetScreenSize();
    const isMobile = screenSize <= ScreenSize.SmallDesktop;

    const navigationOffset = isMobile ? NAVIGATION_MOBILE_OFFSET : NAVIGATION_OFFSET;

    const [positionState, setPositionState] = useState<NavigationPositionState>({
        position: NavigationPosition.Fixed,
        bottom: navigationOffset,
    });

    const updatePosition = useCallback(() => {
        if (typeof window === 'undefined' || !footerRef?.current) {
            return;
        }

        const footer = footerRef.current;
        const footerTop = footer.getBoundingClientRect().top;

        const viewportHeight = window.innerHeight;

        // Footer полностью за пределами viewport
        if (footerTop >= viewportHeight + NAVIGATION_OFFSET - navigationOffset) {
            setPositionState({
                position: NavigationPosition.Fixed,
                bottom: navigationOffset,
            });

            return;
        }

        // Footer виден - используем absolute позиционирование

        setPositionState({
            position: NavigationPosition.Absolute,
            bottom: 0,
        });
    }, [footerRef, navigationOffset]);

    useWindowScroll(updatePosition, 0, { passive: true });

    useEffect(updatePosition, [updatePosition]);

    // Отслеживание видимости Footer с помощью IntersectionObserver
    useEffect(() => {
        if (typeof window === 'undefined' || !footerRef?.current) {
            return;
        }

        const observer = new IntersectionObserver(updatePosition, {
            root: null,
            rootMargin: '0px',
            threshold: [0, 0.1, 0.5, 1],
        });

        observer.observe(footerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [footerRef, updatePosition]);

    return positionState;
};
